import type { AgentCard } from '@ralph/contracts'
import clsx from 'clsx'
import { ArrowUp, ChevronDown } from 'lucide-react'
import { type RefObject, useEffect } from 'react'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { AgentPicker } from '@/components/marketplace/consumer/AgentPicker.tsx'
import { Button, ButtonChip } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * The home composer: the one control the landing screen is built around.
 *
 * It wears the product's single piece of ornament — an iridescent hairline around the card
 * and the same gradient blurred out behind it — because this is where the model is addressed.
 * Nothing else on this screen may wear it.
 *
 * **No attach button.** The reference design draws a paperclip here, and the API has no
 * attachments to give it: `Message.attachments` comes back empty from every endpoint and
 * nothing on the server accepts a file on a message. A control that opens a picker and then
 * drops the file is worse than an absent one, so it is absent.
 *
 * The agent pill is what became of the old radio picker. A conversation is pinned to its
 * agent at creation and can never be repointed (AGM-007/F-10), so the choice has to be made
 * here, before the first message.
 *
 * The list behind that pill is `AgentPicker`, the same menu the chat header opens (PRD-19
 * NAV-030). It replaced a transparent native `<select>`, which could show a column of names
 * and nothing else — no identity tile, no Official mark, no statement that the choice is
 * permanent. That was the wrong amount of information for the one decision on this screen that
 * cannot be taken back.
 *
 * **The pill is always the picker, even with one agent to list.** It used to fall back to a
 * plain label in that case, on the theory that a menu with a single option is a menu that does
 * nothing. But one published agent is what a fresh deployment has, so the fallback was not an
 * edge case — it was the default experience, and it left the most prominent identity on the
 * screen looking pressable and doing nothing. The menu answers the question either way: who
 * answers, that the choice is permanent, and where the other agents come from.
 */

type HomeComposerProps = {
  /** Every agent this viewer can start a conversation with, default first. */
  agents: AgentCard[]
  chosen: AgentCard | null
  onChoose: (agentId: string) => void
  draft: string
  onDraft: (value: string) => void
  onAsk: () => void
  /** The create mutation is in flight. */
  busy: boolean
  /** The listing query has settled, so the agent the conversation will be pinned to is known. */
  ready: boolean
  inputRef: RefObject<HTMLTextAreaElement | null>
}

/** The clamp the autosize honours. Kept in step with `max-h-[176px]` on the textarea below. */
const MAX_HEIGHT = 176

export const HomeComposer = ({
  agents,
  chosen,
  onChoose,
  draft,
  onDraft,
  onAsk,
  busy,
  ready,
  inputRef
}: HomeComposerProps) => {
  // In the body, not at module scope: the label on this control is the one a screen reader
  // reads out, and a capture at import would keep reading the previous language (PRD-27 F-3).
  const t = useT().home
  // The picker's prose is the marketplace catalogue's, because the same control is rendered
  // from the chat header with entries that have to keep saying the same thing (LOC-012).
  const picker = useT().marketplace.gallery.picker

  /*
   * Autosize from the value rather than from `onInput`. A starter chip sets the text
   * programmatically and an input event never fires for it, so a listener-based version leaves
   * a three-line question scrolled out of sight inside a one-line box — which is exactly what
   * the reader is being asked to check before they send it.
   */
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    // Cleared rather than measured when there is nothing in it, so the box shrinks all the way
    // back after a question leaves instead of keeping the height that question needed.
    if (draft === '') {
      el.style.height = ''
      return
    }
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
  }, [draft, inputRef])

  return (
    <div className="group rise relative mt-[22px] w-full max-w-[648px]" style={{ animationDelay: '180ms' }}>
      {/* The halo, then the hairline edge, then the card that covers all but 1.5px of it. */}
      <span
        aria-hidden
        className="absolute -inset-4 rounded-[34px] bg-[image:var(--irid)] opacity-[0.18] blur-[20px] transition-opacity duration-300 group-focus-within:opacity-30"
      />
      <span
        aria-hidden
        className="absolute -inset-[1.5px] rounded-[23px] bg-[image:var(--irid)] opacity-85 transition-opacity duration-300 group-focus-within:opacity-100"
      />

      <div className="relative rounded-[21px] bg-card-solid px-[17px] pb-[11px] pt-[15px] shadow-lift">
        {/*
          `focus-visible:shadow-none` is load-bearing: the app's focus style is a box-shadow
          ring, and a browser matches `:focus-visible` on a text field however it was focused.
          Without this, clicking into the composer draws a ring around a borderless textarea
          in the middle of a card that is already glowing. The card's own edge brightens
          instead, which is the same information at the right scale.
        */}
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={(event) => {
            // `isComposing` guards an IME: committing a candidate with Enter must not send
            // the half-typed sentence underneath it.
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
            event.preventDefault()
            onAsk()
          }}
          rows={1}
          aria-label={t.composerLabel}
          placeholder={t.composerPlaceholder}
          className="block max-h-[176px] min-h-[50px] w-full resize-none border-0 bg-transparent p-0 text-[15.5px] leading-[1.6] tracking-[-0.008em] text-fg placeholder:text-fg-subtle focus:outline-none focus-visible:shadow-none"
        />

        <div className="flex items-center gap-[7px]">
          {chosen && (
            <AgentPicker
              agents={agents}
              currentId={chosen.agentId}
              onPick={onChoose}
              triggerLabel={picker.homeTrigger(chosen.displayName)}
              title={picker.homeTitle}
              hint={agents.length > 1 ? picker.homeHint : `${picker.homeHint} ${picker.alone}`}
              currentLabel={picker.homeCurrent}
              triggerClassName={clsx(PILL, 'text-fg-muted hover:border-rule-strong hover:text-fg')}
              trigger={
                <>
                  <AgentAvatar displayName={chosen.displayName} icon={chosen.icon} accent={chosen.accent} size="sm" />
                  <span className="truncate font-mono text-[9.5px] tracking-[0.05em]">{chosen.displayName}</span>
                  <ChevronDown size={10} strokeWidth={2.2} className="shrink-0" aria-hidden />
                </>
              }
            />
          )}

          <Button
            variant="primary"
            onClick={onAsk}
            disabled={draft.trim() === '' || busy || !ready}
            className="ml-auto"
            trailing={
              <ButtonChip>
                <ArrowUp size={13} strokeWidth={2.4} aria-hidden />
              </ButtonChip>
            }
          >
            {busy ? t.asking : t.ask}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** The picker's trigger: the agent's tile, its name in the mono register, and a chevron. */
const PILL =
  'flex h-[30px] min-w-0 items-center gap-1.5 rounded-full border border-rule pl-[3px] pr-2.5 transition-[color,border-color] duration-200'
